import { useState, useRef, useEffect, useCallback } from 'react'
import { useChatContext } from '../../contexts/ChatContext'
import { useChat } from '../../hooks/useChat'
import { useProfile } from '../../hooks/useProfile'
import { ChatService } from '../../services/chat.service'
import { ChatMessage } from '../../types/chat'
import ChatMessageList from './ChatMessageList'
import ChatInput from './ChatInput'
import ChannelManagement from './ChannelManagement'

export default function ChatContainer() {
  const { isOpen, currentRoom, availableRooms, roomLoading, roomError, switchRoom, refreshRooms, unreadCounts, soundEnabled, setSoundEnabled, soundVolume, setSoundVolume } = useChatContext()
  const { profile } = useProfile()
  const isAdmin = profile?.account_type === 'admin' || profile?.account_type === 'global_admin'
  const [showChannelManagement, setShowChannelManagement] = useState(false)
  const [showRoomSelector, setShowRoomSelector] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ChatMessage[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const {
    messages,
    loading,
    error,
    hasMore,
    sendMessage,
    loadMore,
    editMessage,
    deleteMessage,
  } = useChat({
    roomId: currentRoom?.id || null,
    enabled: isOpen && !!currentRoom,
  })

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!value.trim()) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    debounceRef.current = setTimeout(async () => {
      if (!currentRoom) return
      try {
        const results = await ChatService.searchMessagesPartial(currentRoom.id, value)
        setSearchResults(results)
      } catch (err) {
        console.error('[Chat] Search error:', err)
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
  }, [currentRoom])

  // Close search and reset state
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false)
    setSearchQuery('')
    setSearchResults([])
    setSearchLoading(false)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
  }, [])

  // Reset search when switching rooms
  useEffect(() => {
    closeSearch()
  }, [currentRoom?.id, closeSearch])

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen) {
      // Small delay to allow DOM to render
      setTimeout(() => searchInputRef.current?.focus(), 50)
    }
  }, [isSearchOpen])

  // Determine what to show in the message list
  const isSearchActive = isSearchOpen && searchQuery.trim().length > 0
  const displayMessages = isSearchActive ? searchResults : messages

  if (!isOpen) return null

  return (
    <div className="fixed bottom-24 right-6 z-40 w-80 sm:w-96 h-[450px] max-h-[60vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="bg-primary text-white px-4 py-3 flex items-center justify-between">
        {/* Room selector */}
        <div className="flex-1 min-w-0 relative">
          <button
            onClick={() => availableRooms.length > 1 && setShowRoomSelector(!showRoomSelector)}
            className={`text-left w-full ${availableRooms.length > 1 ? 'cursor-pointer hover:bg-purple-800 -mx-2 px-2 py-1 rounded transition-colors' : ''}`}
            disabled={availableRooms.length <= 1}
          >
            <div className="flex items-center gap-1">
              <h3 className="font-semibold text-sm truncate">
                {currentRoom?.name || 'Chat'}
              </h3>
              {availableRooms.length > 1 && (
                <svg className={`w-4 h-4 flex-shrink-0 transition-transform ${showRoomSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </div>
            {currentRoom?.description && (
              <p className="text-xs text-purple-200 truncate">
                {currentRoom.description}
              </p>
            )}
          </button>

          {/* Room dropdown */}
          {showRoomSelector && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50 max-h-48 overflow-y-auto">
              {availableRooms.map((room) => {
                const roomUnread = unreadCounts.get(room.id) || 0
                return (
                  <button
                    key={room.id}
                    onClick={() => {
                      switchRoom(room.id)
                      setShowRoomSelector(false)
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                      room.id === currentRoom?.id ? 'bg-purple-50 text-purple-900' : 'text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium truncate">{room.name}</div>
                      {roomUnread > 0 && (
                        <span className="flex-shrink-0 bg-red-500 text-white text-xs font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center">
                          {roomUnread > 99 ? '99+' : roomUnread}
                        </span>
                      )}
                    </div>
                    {room.description && (
                      <div className="text-xs text-gray-500 truncate">{room.description}</div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Search button */}
        <button
          onClick={() => isSearchOpen ? closeSearch() : setIsSearchOpen(true)}
          className={`p-1.5 rounded transition-colors ml-2 ${isSearchOpen ? 'bg-purple-700' : 'hover:bg-purple-800'}`}
          title={isSearchOpen ? 'Close search' : 'Search messages'}
        >
          {isSearchOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>

        {/* Sound toggle button with volume control */}
        <div className="relative ml-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            onContextMenu={(e) => {
              e.preventDefault()
              setShowVolumeSlider(!showVolumeSlider)
            }}
            className={`p-1.5 rounded transition-colors ${soundEnabled ? 'bg-purple-700' : 'hover:bg-purple-800'}`}
            title={soundEnabled ? 'Click to mute, right-click for volume' : 'Click to unmute, right-click for volume'}
          >
            {soundEnabled ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            )}
          </button>

          {/* Volume slider dropdown */}
          {showVolumeSlider && (
            <div className="absolute top-full right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-3 z-50 w-32">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={soundVolume}
                  onChange={(e) => setSoundVolume(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
              </div>
              <div className="text-xs text-gray-500 text-center mt-1">
                {Math.round(soundVolume * 100)}%
              </div>
              <button
                onClick={() => setShowVolumeSlider(false)}
                className="w-full mt-2 text-xs text-purple-600 hover:text-purple-800"
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Admin settings button */}
        {isAdmin && (
          <button
            onClick={() => setShowChannelManagement(true)}
            className="p-1.5 hover:bg-purple-800 rounded transition-colors ml-2"
            title="Manage Channels"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        )}
      </div>

      {/* Search bar */}
      {isSearchOpen && (
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && closeSearch()}
              placeholder="Search messages..."
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => handleSearchChange('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchLoading && (
            <div className="text-xs text-gray-400 mt-1 text-center">Searching...</div>
          )}
        </div>
      )}

      {/* Error states */}
      {roomError && (
        <div className="bg-red-50 text-red-600 px-4 py-2 text-sm">
          {roomError}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Loading state for room */}
      {roomLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500 text-sm">Loading chat...</div>
        </div>
      ) : !currentRoom ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500 text-sm text-center px-4">
            <p>Chat is not available.</p>
            <p className="mt-1 text-xs">Please contact an administrator.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Message list */}
          <ChatMessageList
            messages={displayMessages}
            loading={isSearchActive ? searchLoading : loading}
            hasMore={isSearchActive ? false : hasMore}
            highlightQuery={isSearchActive ? searchQuery : undefined}
            searchMode={isSearchActive}
            onLoadMore={loadMore}
            onDeleteMessage={deleteMessage}
            onEditMessage={editMessage}
            isAdmin={isAdmin}
          />

          {/* Input (hidden during search) */}
          {!isSearchActive && (
            <ChatInput
              onSend={sendMessage}
              disabled={!currentRoom}
            />
          )}
        </>
      )}

      {/* Channel Management Modal */}
      <ChannelManagement
        isOpen={showChannelManagement}
        onClose={() => {
          setShowChannelManagement(false)
          refreshRooms() // Refresh available rooms after channel management changes
        }}
      />
    </div>
  )
}

